/**
 * @fileoverview Payment API Contract (Contract-First Design)
 * @module types/contracts
 *
 * Complete API contract for the Payment module.
 * Single source of truth for all payment-related API definitions.
 *
 * NOTE: This contract is prepared but the Payment module is not yet fully integrated.
 * The module has dependencies on services that need to be created first:
 * - TestResult entity
 * - Payment entity
 * - Stripe integration
 */

import { z } from 'zod';
import { createContract, ApiSuccessResponseSchema } from './contract.utils';

// ============================================================================
// ENUMS
// ============================================================================

/**
 * Payment Tier Enum (for pricing)
 * Note: Only premium tier is available
 */
export enum PaymentTier {
	PREMIUM = 'premium',
}

/**
 * Payment Status Enum
 */
export enum PaymentStatus {
	PENDING = 'pending',
	PROCESSING = 'processing',
	SUCCEEDED = 'succeeded',
	FAILED = 'failed',
	CANCELED = 'canceled',
	REQUIRES_ACTION = 'requires_action',
}

/**
 * Payment Method Enum
 */
export enum PaymentMethod {
	STRIPE = 'stripe',
	FREE = 'free',
}

// ============================================================================
// SCHEMAS (Data Structure & Validation)
// ============================================================================

/**
 * Create Payment Intent Request Schema
 */
export const CreatePaymentIntentSchema = z.object({
	testId: z.string().min(1, 'Test ID is required'),
	customerEmail: z.email('Invalid email format'),
	analysisType: z.enum(PaymentTier),
	amount: z.number().int().positive().optional(),
	currency: z.string().length(3).optional(),
	metadata: z.record(z.string(), z.unknown()).optional(),
});
export type CreatePaymentIntentPayload = z.infer<typeof CreatePaymentIntentSchema>;
// Backward compatibility alias
export type PaymentIntentRequest = CreatePaymentIntentPayload;

/**
 * Payment Intent Response Schema
 */
export const PaymentIntentResponseSchema = z.object({
	paymentIntentId: z.string(),
	clientSecret: z.string(),
	amount: z.number().int(),
	currency: z.string(),
	status: z.string(),
});
export type PaymentIntentResponse = z.infer<typeof PaymentIntentResponseSchema>;

/**
 * Verify Payment Request Schema
 */
export const VerifyPaymentSchema = z.object({
	paymentIntentId: z.string().min(1, 'Payment intent ID is required'),
});
export type VerifyPaymentPayload = z.infer<typeof VerifyPaymentSchema>;

/**
 * Verify Payment Response Schema
 */
export const VerifyPaymentResponseSchema = z.object({
	verified: z.boolean(),
	paymentIntentId: z.string(),
	metadata: z.record(z.string(), z.unknown()).optional(),
});
export type VerifyPaymentResponse = z.infer<typeof VerifyPaymentResponseSchema>;

/**
 * Payment Test Data Schema (for process payment)
 */
export const PaymentTestDataSchema = z.object({
	testId: z.string(),
	basicInfo: z.object({
		childName: z.string(),
		childAge: z.number().int().positive(),
		childSchool: z.string(),
		childSituation: z.string(),
	}),
	scores: z.record(z.string(), z.number()),
	answers: z.record(z.string(), z.unknown()),
});
export type PaymentTestData = z.infer<typeof PaymentTestDataSchema>;

/**
 * Process Payment Request Schema
 */
export const ProcessPaymentSchema = z.object({
	paymentIntentId: z.string().min(1, 'Payment intent ID is required'),
	customerEmail: z.email('Invalid email format'),
	testData: PaymentTestDataSchema,
});
export type ProcessPaymentPayload = z.infer<typeof ProcessPaymentSchema>;
// Backward compatibility alias
export type ProcessPaymentRequest = ProcessPaymentPayload;

/**
 * Process Payment Response Schema
 */
export const ProcessPaymentResponseSchema = z.object({
	userId: z.uuid(),
	paymentId: z.uuid(),
	testResultId: z.string(),
	workflowId: z.uuid(),
	analysisSubmitted: z.boolean(),
});
export type ProcessPaymentResponse = z.infer<typeof ProcessPaymentResponseSchema>;
// Backward compatibility alias
export type PaymentProcessData = ProcessPaymentResponse;

/**
 * Pricing Info Schema
 */
export const PricingInfoSchema = z.object({
	amount: z.number().int(),
	currency: z.string(),
	description: z.string(),
	features: z.array(z.string()),
});
export type PricingInfo = z.infer<typeof PricingInfoSchema>;

/**
 * Get Pricing Response Schema
 * Note: Only premium tier is available
 */
export const GetPricingResponseSchema = z.object({
	premium: PricingInfoSchema,
});
export type GetPricingResponse = z.infer<typeof GetPricingResponseSchema>;

/**
 * Webhook Event Response Schema
 */
export const WebhookEventResponseSchema = z.object({
	success: z.boolean(),
	message: z.string(),
	data: z.unknown().nullable(),
});
export type WebhookEventResponse = z.infer<typeof WebhookEventResponseSchema>;

// ============================================================================
// API RESPONSE WRAPPERS
// ============================================================================

/**
 * Payment Intent API Response
 * { success: true, data: PaymentIntentResponse, timestamp: string }
 */
export const PaymentIntentApiResponseSchema = ApiSuccessResponseSchema(PaymentIntentResponseSchema);

/**
 * Verify Payment API Response
 * { success: true, data: VerifyPaymentResponse, timestamp: string }
 */
export const VerifyPaymentApiResponseSchema = ApiSuccessResponseSchema(VerifyPaymentResponseSchema);

/**
 * Process Payment API Response
 * { success: true, data: ProcessPaymentResponse, timestamp: string }
 */
export const ProcessPaymentApiResponseSchema = ApiSuccessResponseSchema(
	ProcessPaymentResponseSchema,
);

/**
 * Get Pricing API Response
 * { success: true, data: GetPricingResponse, timestamp: string }
 */
export const GetPricingApiResponseSchema = ApiSuccessResponseSchema(GetPricingResponseSchema);

/**
 * Webhook Event API Response
 * { success: true, data: WebhookEventResponse, timestamp: string }
 */
export const WebhookEventApiResponseSchema = ApiSuccessResponseSchema(WebhookEventResponseSchema);

// ============================================================================
// API CONTRACT (Routes, Methods, Request/Response)
// ============================================================================

/**
 * Payment API Contract
 *
 * Defines the complete API surface for the Payment module.
 * Both frontend and backend use this as the single source of truth.
 *
 * NOTE: This contract is prepared but not yet fully implemented.
 * The Payment module requires additional entities and services.
 */
export const PAYMENT_API_CONTRACT = createContract('/payment', {
	/**
	 * POST /payment/create-intent
	 * Create a payment intent for premium analysis
	 */
	createIntent: {
		method: 'POST' as const,
		path: '/create-intent',
		body: CreatePaymentIntentSchema,
		response: PaymentIntentApiResponseSchema,
	},

	/**
	 * POST /payment/verify
	 * Verify if a payment has been completed
	 */
	verify: {
		method: 'POST' as const,
		path: '/verify',
		body: VerifyPaymentSchema,
		response: VerifyPaymentApiResponseSchema,
	},

	/**
	 * POST /payment/process
	 * Process payment completion (create user, link session, submit test)
	 */
	process: {
		method: 'POST' as const,
		path: '/process',
		body: ProcessPaymentSchema,
		response: ProcessPaymentApiResponseSchema,
	},

	/**
	 * POST /payment/webhook
	 * Handle Stripe webhook events
	 */
	webhook: {
		method: 'POST' as const,
		path: '/webhook',
		body: z.unknown(), // Raw Stripe webhook payload
		response: WebhookEventApiResponseSchema,
	},

	/**
	 * GET /payment/pricing
	 * Get pricing information for different analysis types
	 */
	pricing: {
		method: 'GET' as const,
		path: '/pricing',
		response: GetPricingApiResponseSchema,
	},
});

/**
 * Route Constants for Controllers
 * Use these in @Controller() and @Get/@Post/@Delete decorators
 */
export const PAYMENT_ROUTES = {
	base: PAYMENT_API_CONTRACT.base,
	createIntent: PAYMENT_API_CONTRACT.routes.createIntent.path,
	verify: PAYMENT_API_CONTRACT.routes.verify.path,
	process: PAYMENT_API_CONTRACT.routes.process.path,
	pricing: PAYMENT_API_CONTRACT.routes.pricing.path,
	webhook: PAYMENT_API_CONTRACT.routes.webhook.path,
};

// ============================================================================
// TYPE EXPORTS
// ============================================================================

/**
 * Export all types for use in frontend and backend
 */
export type PaymentApiContract = typeof PAYMENT_API_CONTRACT;
